import {parseAuraVideoUrl} from 'components/DSYS/editor/nodes/auraVideoUrl';
import {SCOPED_HTML_HEADING_IMPORT_ATTR} from 'plugins/enhancedHeadings/EnhancedHeadingNode';

/**
 * Rewrites pasted HTML (primarily Dropbox Paper's clipboard format) into the shapes our Lexical
 * importers understand, so a whole Paper doc pastes with its formatting intact.
 *
 * Paper's clipboard HTML is one `<div>` per line with `author-d-*` spans, and encodes a lot of
 * structure in classes and inline styles rather than semantic tags. Each step below maps one
 * Paper construct onto what an existing `importDOM` already handles.
 */

export type PastedHtmlOptions = {
  /** Allowlisted Aura origin/hosts; when set, links alone on a line become Aura embeds. */
  aura?: {embedOrigin: string | undefined; allowedHostnames: readonly string[]};
};

const PAPER_MARKERS = [
  '[class*="author-d-"]',
  '.ace-line',
  '[data-usually-unique-id]',
  'img[data-emoji-ch]',
  '[data-ace-gallery-urls]',
].join(',');

export const isPaperDocument = (root: ParentNode) => root.querySelector(PAPER_MARKERS) !== null;

/** Swap `el` for a new `tagName` element that keeps its children. */
const retag = (el: Element, tagName: string): HTMLElement => {
  const doc = el.ownerDocument;
  const replacement = doc.createElement(tagName);
  while (el.firstChild) {
    replacement.appendChild(el.firstChild);
  }
  el.replaceWith(replacement);
  return replacement;
};

/** The top-level "line" containing `el` (Paper wraps every line in a direct child of body). */
const lineOf = (el: Element, body: HTMLElement): Element => {
  let current: Element = el;
  while (current.parentElement && current.parentElement !== body) {
    current = current.parentElement;
  }
  return current;
};

/** Paper emoji are `<img data-emoji-ch="🙂">` pointing at Paper's CDN; paste the character. */
const replaceEmojiImages = (body: HTMLElement) => {
  body.querySelectorAll('img[data-emoji-ch]').forEach((img) => {
    const ch = img.getAttribute('data-emoji-ch');
    if (ch) {
      img.replaceWith(body.ownerDocument.createTextNode(ch));
    }
  });
};

/**
 * Paper separates a custom emoji image from the next word with a span holding a single space;
 * Lexical drops whitespace-only text that follows an inline image, gluing the two together.
 */
const keepSpaceAfterCustomEmoji = (body: HTMLElement) => {
  body.querySelectorAll('[data-ace-content-type-id="customEmoji"]').forEach((emoji) => {
    const host = emoji.parentElement?.matches('span') ? emoji.parentElement : emoji;
    const next = host.nextElementSibling;
    if (next && next.children.length === 0 && next.textContent === ' ') {
      next.textContent = '\u00a0';
    }
  });
};

/** Floated images: `style="float: left"` on the img and/or an `.ace-float-*` line wrapper. */
const markFloatedImages = (body: HTMLElement) => {
  body.querySelectorAll('img').forEach((img) => {
    const float = img.style.float || '';
    const wrapper = img.closest('.ace-float-left, .ace-float-right');
    const layout =
      float === 'left' || float === 'right'
        ? float
        : wrapper?.classList.contains('ace-float-left')
          ? 'left'
          : wrapper?.classList.contains('ace-float-right')
            ? 'right'
            : null;
    if (layout) {
      img.setAttribute('data-layout', layout);
    }
  });
};

/**
 * Paper renders a line that is entirely bold as a sub-heading (`ace-all-bold-hthree`), and
 * h3/h4 are otherwise ignored by our heading importer unless explicitly opted in.
 */
const convertHeadings = (body: HTMLElement) => {
  body.querySelectorAll('.ace-all-bold-hthree').forEach((span) => {
    retag(span, 'h3');
  });
  body
    .querySelectorAll('h3, h4')
    .forEach((heading) => heading.setAttribute(SCOPED_HTML_HEADING_IMPORT_ATTR, 'true'));
};

/** Paper marks the header row with a class on `<table>`; its cells are plain `<td>`. */
const convertTableHeaders = (body: HTMLElement) => {
  body.querySelectorAll('table.table-top-header').forEach((table) => {
    const firstRow = table.querySelector('tr');
    firstRow?.querySelectorAll(':scope > td').forEach((td) => retag(td, 'th'));
  });
};

/** Task lists: `ul.listtype-task` + `li[data-task-done]` with a decorative checkbox span. */
const convertTaskLists = (body: HTMLElement) => {
  body.querySelectorAll('ul.listtype-task').forEach((ul) => {
    ul.setAttribute('__lexicallisttype', 'check');
    ul.querySelectorAll(':scope > li').forEach((li) => {
      li.setAttribute(
        'aria-checked',
        li.getAttribute('data-task-done') === 'true' ? 'true' : 'false'
      );
    });
  });
};

/** Quotes are a list (`ul.listtype-quote`) of lines; Lexical wants one blockquote with breaks. */
const convertQuotes = (body: HTMLElement) => {
  body.querySelectorAll('ul.listtype-quote').forEach((ul) => {
    const doc = body.ownerDocument;
    const quote = doc.createElement('blockquote');
    ul.querySelectorAll(':scope > li').forEach((li, index) => {
      if (index > 0) {
        quote.appendChild(doc.createElement('br'));
      }
      while (li.firstChild) {
        quote.appendChild(li.firstChild);
      }
    });
    ul.replaceWith(quote);
  });
};

const convertHighlights = (body: HTMLElement) => {
  body.querySelectorAll('span.highlight, span[data-highlight-color]').forEach((span) => {
    retag(span, 'mark');
  });
};

/** Paper nests `<hr>` inside spans inside the line div; Lexical needs it at block level. */
const hoistDividers = (body: HTMLElement) => {
  body.querySelectorAll('hr').forEach((hr) => {
    const line = lineOf(hr, body);
    if (line !== hr && !line.textContent?.trim()) {
      line.replaceWith(body.ownerDocument.createElement('hr'));
    }
  });
};

/** Paper's blank lines are `<div><br></div>`; keep them as empty paragraphs for spacing. */
const preserveBlankLines = (body: HTMLElement) => {
  body.querySelectorAll('div').forEach((div) => {
    const children = Array.from(div.childNodes);
    if (children.length === 1 && children[0].nodeName === 'BR') {
      const paragraph = body.ownerDocument.createElement('p');
      paragraph.appendChild(body.ownerDocument.createElement('br'));
      div.replaceWith(paragraph);
    }
  });
};

/** Paper @mention links (`/ep/profile/…`) aren't meaningful here; keep their text. */
const unlinkProfileMentions = (body: HTMLElement) => {
  body.querySelectorAll('a[href*="/ep/profile/"]').forEach((link) => {
    link.replaceWith(body.ownerDocument.createTextNode(link.textContent ?? ''));
  });
};

/** A line that is nothing but an Aura link (as Paper shows its embeds) becomes an Aura embed. */
const embedStandaloneAuraLinks = (body: HTMLElement, aura: PastedHtmlOptions['aura']) => {
  if (!aura) {
    return;
  }
  body.querySelectorAll('a[href]').forEach((link) => {
    const parsed = parseAuraVideoUrl(
      link.getAttribute('href') ?? '',
      aura.embedOrigin,
      aura.allowedHostnames
    );
    if (!parsed) {
      return;
    }
    const line = lineOf(link, body);
    if (line.textContent?.trim() !== link.textContent?.trim()) {
      return;
    }
    const iframe = body.ownerDocument.createElement('iframe');
    iframe.setAttribute('data-lexical-aura-video', parsed.videoRef);
    iframe.setAttribute('data-lexical-aura-origin', parsed.origin);
    line.replaceWith(iframe);
  });
};

/** Elements Paper marks as editor chrome that shouldn't be pasted (e.g. task checkboxes). */
const removeEditorChrome = (body: HTMLElement) => {
  body.querySelectorAll('.ace-copy-paste-hide').forEach((el) => el.remove());
};

export const normalizePastedHtml = (html: string, options: PastedHtmlOptions = {}): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const {body} = doc;

  unlinkProfileMentions(body);
  removeEditorChrome(body);
  replaceEmojiImages(body);
  keepSpaceAfterCustomEmoji(body);
  markFloatedImages(body);
  convertHeadings(body);
  convertTableHeaders(body);
  convertTaskLists(body);
  convertQuotes(body);
  convertHighlights(body);
  hoistDividers(body);
  embedStandaloneAuraLinks(body, options.aura);
  preserveBlankLines(body);

  return body.innerHTML;
};
